import { useTranslation } from 'react-i18next';
import { ScheduleTaskType, parseDateOnly, scheduleTaskWorkdays } from '@construction/shared';
import type { ScheduleData, ScheduleTaskData } from '@/api/endpoints';
import { ListGroup, ListRow, Tag } from '@/components/ui';
import { activeOn, ganttRange, projectWeek } from './gantt-layout';

const TYPE_TONE: Record<ScheduleTaskType, 'brand' | 'warn' | 'neutral'> = {
  WORK: 'brand',
  INSPECTION: 'warn',
  NONWORK: 'neutral',
};

/**
 * 进度表的列表视图（CR-082，手机默认）：「今天」一组（在做的 + 下一项），下面按月列全部任务。
 *
 * 手机上不硬塞甘特图：三百多像素宽的屏幕上一条条细线，看得出哪天在干什么，看不出干的是什么。
 * 工地上的人要回答的是「今天该干到哪了、接下来是什么」—— 列表直接回答；甘特图在上面的开关里一下就切过去。
 */
export function ScheduleTaskList({ data, today, workdays }: { data: ScheduleData; today: string; workdays: number[] }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const nonWork = new Set(data.calendar.nonWorkDates);
  const md = (key: string) =>
    new Date(`${key}T00:00:00Z`).toLocaleDateString(lang, { timeZone: 'UTC', month: 'numeric', day: 'numeric' });
  const monthTitle = (key: string) =>
    new Date(`${key}T00:00:00Z`).toLocaleDateString(lang, { timeZone: 'UTC', year: 'numeric', month: 'long' });
  const daysUntil = (key: string) => {
    const a = parseDateOnly(today);
    const b = parseDateOnly(key);
    return a === null || b === null ? 0 : Math.round((b - a) / 86_400_000);
  };

  const sorted = [...data.tasks].sort((a, b) =>
    a.plannedStart === b.plannedStart ? a.code.localeCompare(b.code) : a.plannedStart < b.plannedStart ? -1 : 1,
  );
  const doing = activeOn(sorted, today);
  const range = ganttRange(sorted);
  const week = range ? projectWeek(range, today) : null;
  const doingIds = new Set(doing.map((task) => task.id));
  const upcoming = sorted.find((task) => task.type !== ScheduleTaskType.NONWORK && task.plannedStart > today);

  const span = (task: ScheduleTaskData) =>
    task.plannedFinish === task.plannedStart ? md(task.plannedStart) : `${md(task.plannedStart)} – ${md(task.plannedFinish)}`;
  const subtitle = (task: ScheduleTaskData, extra?: string | null) =>
    [
      task.code,
      span(task),
      extra ?? (task.workdays !== null ? t('schedule.gantt.workdays', { count: task.workdays }) : null),
    ]
      .filter(Boolean)
      .join(' · ');

  // 在做的第几个工作日：从开工数到今天，停工日不算
  const dayOf = (task: ScheduleTaskData) => {
    const done = scheduleTaskWorkdays({ type: task.type, plannedStart: task.plannedStart, plannedFinish: today }, workdays, nonWork);
    return done !== null && task.workdays !== null ? t('schedule.dayOf', { day: done, total: task.workdays }) : null;
  };

  const months = new Map<string, ScheduleTaskData[]>();
  for (const task of sorted) {
    const key = task.plannedStart.slice(0, 7);
    months.set(key, [...(months.get(key) ?? []), task]);
  }

  return (
    <div className="flex flex-col gap-5">
      <ListGroup
        title={[
          t('schedule.todayTitle', { date: md(today) }),
          week ? t('schedule.weekOf', { week: week.week, total: Math.ceil(week.total) }) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      >
        {doing.length === 0 && !upcoming && (
          <p className="px-4 py-3 text-body text-ink-3">{t('schedule.todayNothing')}</p>
        )}
        {doing.map((task) => (
          <ListRow
            key={task.id}
            title={task.name}
            subtitle={subtitle(task, dayOf(task))}
            trailing={<Tag tone="brand">{t('overview.inProgress')}</Tag>}
          />
        ))}
        {upcoming && (
          <ListRow
            title={upcoming.name}
            subtitle={subtitle(upcoming)}
            trailing={
              <Tag tone="neutral">
                {daysUntil(upcoming.plannedStart) === 1
                  ? t('overview.startsTomorrow')
                  : t('overview.startsIn', { days: daysUntil(upcoming.plannedStart) })}
              </Tag>
            }
          />
        )}
      </ListGroup>

      {[...months.entries()].map(([key, tasks]) => (
        <ListGroup key={key} title={monthTitle(`${key}-01`)}>
          {tasks.map((task) => (
            <ListRow
              key={task.id}
              selected={doingIds.has(task.id)}
              title={task.name}
              subtitle={subtitle(task)}
              trailing={<Tag tone={TYPE_TONE[task.type]}>{t(`schedule.type.${task.type}`)}</Tag>}
            />
          ))}
        </ListGroup>
      ))}
    </div>
  );
}
